/**
 * CSR-762 — The Retention Agent field on insureds is sourced from NowCerts
 * via the Data Center sync. CSRs who leave the team continue to appear as
 * retention agents (e.g. "Erika Armstrong") because the upstream NowCerts
 * record never gets updated. We canonicalize incoming names against this
 * authoritative team list so only current members are persisted; everything
 * else (including legacy stale values) collapses to `null`.
 *
 * Used by:
 *   - `server/sync.ts` — filters DC payloads before calling
 *     `upsertInsuredFromSync`.
 *   - `client/src/components/insured/EditInsuredDialog.tsx` — backs the
 *     Retention Agent dropdown so the UI cannot save invalid values.
 *
 * Keep this list in sync with the actual team. Adding/removing a member
 * here is the single source of truth for both server-side filtering and
 * the client-side dropdown.
 */
export const RETENTION_AGENT_TEAM = [
  // CSR-1862 — canonical is the team's everyday name (her key is LILY_SEALY
  // across systems); the formal NowCerts spelling lives in the aliases below.
  "Lily Sealy",
  "Austin Armstrong",
  "Nick Bullard",
  "Ryan Floyd",
] as const;

export type RetentionAgent = (typeof RETENTION_AGENT_TEAM)[number];

/**
 * CSR-1305 — NowCerts records the same person under more than one spelling
 * (e.g. "Nicholas Bullard" for "Nick Bullard", "Lily Sealy" for "Lillian
 * Sealy"). We fold each known variant into its single canonical team name so
 * the Retention Agent has exactly one option and no assignment is dropped on
 * sync. Keys are normalized (lowercase, single-spaced).
 */
const RETENTION_AGENT_ALIASES: Record<string, RetentionAgent> = {
  "nicholas bullard": "Nick Bullard",
  "lillian sealy": "Lily Sealy",
};

/**
 * Returns the canonical team-list spelling of `name` if it matches a current
 * Retention Agent (case- and whitespace-insensitive) or a known variant of
 * one (see `RETENTION_AGENT_ALIASES`), otherwise `null`.
 *
 * Empty / nullish input returns `null`. This is the only place the matching
 * rule lives — callers should never re-implement it.
 */
export function canonicalizeRetentionAgent(
  name: string | null | undefined,
): RetentionAgent | null {
  if (name == null) return null;
  const normalized = name.replace(/\s+/g, " ").trim().toLowerCase();
  if (normalized === "") return null;
  for (const member of RETENTION_AGENT_TEAM) {
    if (member.toLowerCase() === normalized) return member;
  }
  return RETENTION_AGENT_ALIASES[normalized] ?? null;
}
